import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, MoreVertical } from "lucide-react";
import { useNotifications } from "../../context/NotificationContext";
import { getNotificationCategories } from "../../services/notificationService";
import NotificationRow from "../../components/notifications/NotificationRow";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import ConfirmModal from "../../components/common/ConfirmModal";
import Pagination from "../../components/common/Pagination";
import { color, radius, shadow } from "../../styles/theme";

/**
 * Full notification center — web port of mobile's Screens/Notifications.
 * Category filter chips, expand/collapse rows, mark-all-read / delete-all
 * via an options menu, "Load more" pagination (this panel's established
 * list pattern, in place of mobile's infinite-scroll FlatList).
 */
const NotificationsPage = () => {
  const { t } = useTranslation(["notifications", "common"]);
  const notif = useNotifications();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "markAllRead" | "deleteAll" | null
  const [confirmBusy, setConfirmBusy] = useState(false);
  const menuRef = useRef(null);

  const { notifications, loading, loadingMore, page, pageSize, meta, load, markRead, removeOne, markAllRead, clearAll } = notif || {};

  useEffect(() => {
    getNotificationCategories()
      .then((res) => setCategories(res.data?.Result?.Items || []))
      .catch(() => {});
  }, []);

  // The list is loaded lazily (not eagerly by NotificationContext, see its
  // comments) — visiting this page is a deliberate "view my notifications"
  // action, so load it here.
  useEffect(() => {
    load?.(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (!notif) return null;

  const selectCategory = (categoryId) => {
    setActiveCategory(categoryId);
    load(1, categoryId ? String(categoryId) : undefined);
  };

  const runConfirmedAction = async () => {
    setConfirmBusy(true);
    try {
      if (confirmAction === "markAllRead") await markAllRead();
      else if (confirmAction === "deleteAll") await clearAll();
    } finally {
      setConfirmBusy(false);
      setConfirmAction(null);
    }
  };

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.title}>{t("page.title")}</h2>
        {notifications.length > 0 && (
          <div ref={menuRef} style={{ position: "relative" }}>
            <button type="button" className="btn-ghost" style={s.menuBtn} onClick={() => setMenuOpen((v) => !v)} aria-label={t("page.options")}>
              <MoreVertical size={18} strokeWidth={2} />
            </button>
            {menuOpen && (
              <div className="modal-pop" style={s.menu}>
                <button className="row-link" style={s.menuItem} onClick={() => { setMenuOpen(false); setConfirmAction("markAllRead"); }}>
                  {t("page.markAllRead")}
                </button>
                <button className="row-link" style={{ ...s.menuItem, color: color.danger500 }} onClick={() => { setMenuOpen(false); setConfirmAction("deleteAll"); }}>
                  {t("page.deleteAll")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={s.chipsRow}>
        <button
          type="button"
          style={{ ...s.chip, ...(activeCategory === null ? s.chipActive : {}) }}
          onClick={() => selectCategory(null)}
        >
          {t("page.allCategories")}
        </button>
        {categories.map((cat) => (
          <button
            key={cat.Id}
            type="button"
            style={{ ...s.chip, ...(activeCategory === cat.Id ? s.chipActive : {}) }}
            onClick={() => selectCategory(cat.Id)}
          >
            {cat.DisplayName}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : notifications.length === 0 ? (
        <EmptyState icon={Bell} text={t("page.empty")} />
      ) : (
        <div style={s.list}>
          {notifications.map((item) => (
            <NotificationRow key={item.Id} item={item} onMarkRead={markRead} onDelete={removeOne} />
          ))}
        </div>
      )}

      {notifications.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={meta?.TotalCount ?? 0}
          onPageChange={(p) => load(p)}
          disabled={loadingMore}
        />
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction === "markAllRead" ? t("page.markAllReadConfirmTitle") : t("page.deleteAllConfirmTitle")}
          message={confirmAction === "markAllRead" ? t("page.markAllReadConfirmMessage") : t("page.deleteAllConfirmMessage")}
          danger={confirmAction === "deleteAll"}
          loading={confirmBusy}
          onConfirm={runConfirmedAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
};

const s = {
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  title: { fontSize: 20, fontWeight: 800, color: color.textPrimary, margin: 0, letterSpacing: -0.3 },
  menuBtn: { width: 34, height: 34, padding: 0, borderRadius: radius.sm },
  menu: {
    position: "absolute", top: "calc(100% + 6px)", right: 0, width: 190,
    background: color.surface, borderRadius: radius.md, boxShadow: shadow.lg,
    border: `1px solid ${color.border}`, overflow: "hidden", zIndex: 150, padding: 4,
  },
  menuItem: {
    width: "100%", display: "block", padding: "9px 12px", background: "transparent", border: "none",
    fontSize: 13, color: color.gray700, fontWeight: 500, cursor: "pointer", textAlign: "left", borderRadius: radius.sm,
  },
  chipsRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 },
  chip: {
    padding: "6px 14px", borderRadius: radius.pill, border: `1px solid ${color.border}`,
    background: color.surface, color: color.gray700, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  },
  chipActive: { background: color.primary500, borderColor: color.primary500, color: "#fff" },
  list: {
    maxWidth: 640, background: color.surface, borderRadius: radius.md,
    border: `1px solid ${color.border}`, overflow: "hidden",
  },
};

export default NotificationsPage;
